import React, { useState, useEffect } from 'react';
import { X, Users, UserPlus, Mail, Search, Check, Minus } from 'lucide-react';
import { addMemberToGroup, inviteToGroup, getGroupById, removeMemberFromGroup, getGroupName } from '../../services/groupsService';
import { useTranslation } from '../../context/LanguageContext';

/**
 * Modal for adding members to an existing group
 */
const AddMembersModal = ({ isOpen, onClose, group, contacts, onMembersAdded }) => {
    const { t, language } = useTranslation();
    const [searchTerm, setSearchTerm] = useState('');
    const [inviteEmail, setInviteEmail] = useState('');
    const [selectedMembers, setSelectedMembers] = useState([]);
    const [invitedEmails, setInvitedEmails] = useState([]);
    const [existingMembers, setExistingMembers] = useState([]);
    const [removedMembers, setRemovedMembers] = useState([]);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    // Load existing members when modal opens
    useEffect(() => {
        if (isOpen && group?.groupUuid) {
            loadExistingMembers();
        }
    }, [isOpen, group?.groupUuid]);

    const loadExistingMembers = async () => {
        setLoading(true);
        try {
            const groupDetails = await getGroupById(group.groupUuid);
            if (groupDetails?.members) {
                setExistingMembers(groupDetails.members);
            }
        } catch (err) {
            console.error('Error loading existing members:', err);
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen || !group) return null;

    // Check if a contact is already a member of the group (and not removed)
    const isExistingMember = (contact) => {
        const isInExisting = existingMembers.some(m =>
            (m.contactId || m.contactid) === contact.id ||
            (m.peopleId || m.peopleid) === contact.peopleId
        );
        const isRemoved = removedMembers.some(m =>
            (m.contactId || m.contactid) === contact.id ||
            (m.peopleId || m.peopleid) === contact.peopleId
        );
        return isInExisting && !isRemoved;
    };

    // Remove an existing member from the group
    const handleRemoveExistingMember = async (member) => {
        const contactId = member.contactId || member.contactid;
        if (!contactId) {
            console.error('No contactId found for member:', member);
            return;
        }

        try {
            await removeMemberFromGroup(group.groupUuid, contactId);
            // Add to removed list and remove from existing
            setRemovedMembers(prev => [...prev, member]);
            setExistingMembers(prev => prev.filter(m =>
                (m.contactId || m.contactid) !== contactId
            ));
        } catch (err) {
            console.error('Error removing member:', err);
            setError(t.groupModal?.errorRemovingMember || 'Error removing member');
        }
    };

    const handleAddMember = async (contact) => {
        if (selectedMembers.some(m => m.id === contact.id)) return;

        setSelectedMembers(prev => [...prev, contact]);

        try {
            await addMemberToGroup(group.groupUuid, contact);
        } catch (err) {
            console.error('Error adding member:', err);
            setError(t.groupModal?.errorAddingMember || 'Error adding member');
        }
    };

    const handleRemoveMember = async (memberId) => {
        // Remove from local state
        setSelectedMembers(prev => prev.filter(m => m.id !== memberId));

        // Also remove from group via API (member was already added)
        try {
            await removeMemberFromGroup(group.groupUuid, memberId);
        } catch (err) {
            console.error('Error removing newly added member:', err);
        }
    };

    const handleInviteByEmail = async () => {
        const email = inviteEmail.trim().toLowerCase();

        if (!email) return;

        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            setError(t.groupModal?.invalidEmail || 'Invalid email address');
            return;
        }

        if (invitedEmails.includes(email)) {
            setError(t.groupModal?.alreadyInvited || 'This address is already invited');
            return;
        }

        if (selectedMembers.some(m => m.email?.toLowerCase() === email)) {
            setError(t.groupModal?.alreadyMember || 'This person is already a member');
            return;
        }

        setInvitedEmails(prev => [...prev, email]);

        try {
            await inviteToGroup(group.groupUuid, email);
        } catch (err) {
            console.error('Error inviting:', err);
            setError(t.groupModal?.errorInviting || 'Error inviting');
        }

        setInviteEmail('');
        setError('');
    };

    const handleRemoveInvitation = (email) => {
        setInvitedEmails(prev => prev.filter(e => e !== email));
    };

    const handleFinish = () => {
        // Notify parent of changes (added + removed)
        const totalChanges = selectedMembers.length + invitedEmails.length + removedMembers.length;
        onMembersAdded?.(totalChanges);
        handleClose();
    };

    const handleClose = () => {
        setSearchTerm('');
        setInviteEmail('');
        setSelectedMembers([]);
        setInvitedEmails([]);
        setExistingMembers([]);
        setRemovedMembers([]);
        setError('');
        onClose();
    };

    // Filter contacts based on search
    const filteredContacts = contacts.filter(c => {
        if (!searchTerm) return true;
        const search = searchTerm.toLowerCase();
        return (
            `${c.firstName} ${c.lastName}`.toLowerCase().includes(search) ||
            c.email?.toLowerCase().includes(search) ||
            c.company?.toLowerCase().includes(search)
        );
    });

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden">
                {/* Header */}
                <div className="bg-gradient-to-r from-linkoo-purple to-linkoo-pink p-6 text-white">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <Users className="w-6 h-6" />
                            <h2 className="text-xl font-bold">
                                {t.groupModal?.addMembers || 'Add members'}
                            </h2>
                        </div>
                        <button
                            onClick={handleClose}
                            className="p-1 hover:bg-white/20 rounded-full transition-colors"
                        >
                            <X className="w-6 h-6" />
                        </button>
                    </div>
                    <p className="mt-2 text-white/80">{t.groupModal?.group || 'Group'}: {getGroupName(group, language)}</p>
                </div>

                {/* Content */}
                <div className="p-6 overflow-y-auto max-h-[60vh]">
                    {error && (
                        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                            {error}
                        </div>
                    )}

                    <div className="space-y-6">
                        {/* Existing members */}
                        {existingMembers.length > 0 && (
                            <div>
                                <h3 className="text-sm font-medium text-gray-700 mb-2">
                                    {t.groupModal?.existingMembers || 'Current members'} ({existingMembers.length})
                                </h3>
                                <div className="flex flex-wrap gap-2">
                                    {existingMembers.map(member => (
                                        <span
                                            key={member.contactId || member.contactid || member.peopleId || member.peopleid}
                                            className="inline-flex items-center gap-1 bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm"
                                        >
                                            <Check className="w-3 h-3" />
                                            {member.firstName || member.firstname} {member.lastName || member.lastname}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* New members to add */}
                        {(selectedMembers.length > 0 || invitedEmails.length > 0) && (
                            <div>
                                <h3 className="text-sm font-medium text-gray-700 mb-2">
                                    {t.groupModal?.newMembers || 'New members'} ({selectedMembers.length + invitedEmails.length})
                                </h3>
                                <div className="flex flex-wrap gap-2">
                                    {selectedMembers.map(member => (
                                        <span
                                            key={member.id}
                                            className="inline-flex items-center gap-1 bg-linkoo-purple/10 text-linkoo-purple px-3 py-1 rounded-full text-sm"
                                        >
                                            {member.firstName} {member.lastName}
                                            <button
                                                onClick={() => handleRemoveMember(member.id)}
                                                className="ml-1 hover:bg-linkoo-purple/20 rounded-full p-0.5"
                                            >
                                                <X className="w-3 h-3" />
                                            </button>
                                        </span>
                                    ))}
                                    {invitedEmails.map(email => (
                                        <span
                                            key={email}
                                            className="inline-flex items-center gap-1 bg-orange-100 text-orange-700 px-3 py-1 rounded-full text-sm"
                                        >
                                            <Mail className="w-3 h-3" />
                                            {email}
                                            <button
                                                onClick={() => handleRemoveInvitation(email)}
                                                className="ml-1 hover:bg-orange-200 rounded-full p-0.5"
                                            >
                                                <X className="w-3 h-3" />
                                            </button>
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Add from Linkoo contacts */}
                        <div>
                            <h3 className="text-sm font-medium text-gray-700 mb-2">
                                {t.groupModal?.addFromContacts || 'Add from my contacts'}
                            </h3>
                            <div className="relative mb-3">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                                <input
                                    type="text"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    placeholder={t.groupModal?.searchContacts || 'Search...'}
                                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-linkoo-purple/20 focus:border-linkoo-purple text-sm"
                                />
                            </div>
                            <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
                                {loading ? (
                                    <p className="p-3 text-sm text-gray-500 text-center">
                                        {t.common?.loading || 'Loading...'}
                                    </p>
                                ) : filteredContacts.length === 0 ? (
                                    <p className="p-3 text-sm text-gray-500 text-center">
                                        {t.groupModal?.noContactsFound || 'No contacts found'}
                                    </p>
                                ) : (
                                    filteredContacts.map(contact => {
                                        const isSelected = selectedMembers.some(m => m.id === contact.id);
                                        const isAlreadyMember = isExistingMember(contact);
                                        // Find the existing member object to get contactId for removal
                                        const existingMemberObj = existingMembers.find(m =>
                                            (m.contactId || m.contactid) === contact.id ||
                                            (m.peopleId || m.peopleid) === contact.peopleId
                                        );

                                        const handleClick = () => {
                                            if (isAlreadyMember && existingMemberObj) {
                                                // Remove from group
                                                handleRemoveExistingMember(existingMemberObj);
                                            } else if (!isSelected) {
                                                // Add to group
                                                handleAddMember(contact);
                                            }
                                        };

                                        return (
                                            <button
                                                key={contact.id}
                                                onClick={handleClick}
                                                disabled={isSelected}
                                                className={`w-full flex items-center justify-between p-3 text-left transition-colors ${
                                                    isAlreadyMember ? 'bg-green-50 hover:bg-red-50' :
                                                    isSelected ? 'bg-linkoo-purple/10 cursor-not-allowed' :
                                                    'hover:bg-gray-50'
                                                }`}
                                            >
                                                <div>
                                                    <p className={`font-medium text-sm ${isAlreadyMember ? 'text-green-700' : 'text-gray-900'}`}>
                                                        {contact.firstName} {contact.lastName}
                                                    </p>
                                                    {contact.email && (
                                                        <p className="text-xs text-gray-500">{contact.email}</p>
                                                    )}
                                                </div>
                                                {isAlreadyMember ? (
                                                    <div className="group relative">
                                                        <Check className="w-5 h-5 text-green-500 group-hover:hidden" />
                                                        <Minus className="w-5 h-5 text-red-500 hidden group-hover:block" />
                                                    </div>
                                                ) : isSelected ? (
                                                    <Check className="w-5 h-5 text-linkoo-purple" />
                                                ) : (
                                                    <UserPlus className="w-5 h-5 text-gray-400" />
                                                )}
                                            </button>
                                        );
                                    })
                                )}
                            </div>
                        </div>

                        {/* Invite by email */}
                        <div>
                            <h3 className="text-sm font-medium text-gray-700 mb-2">
                                {t.groupModal?.inviteByEmail || 'Invite by email'}
                            </h3>
                            <div className="flex gap-2">
                                <div className="relative flex-1">
                                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                                    <input
                                        type="email"
                                        value={inviteEmail}
                                        onChange={(e) => setInviteEmail(e.target.value)}
                                        onKeyPress={(e) => e.key === 'Enter' && handleInviteByEmail()}
                                        placeholder={t.groupModal?.emailPlaceholder || 'address@email.com'}
                                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-linkoo-purple/20 focus:border-linkoo-purple text-sm"
                                    />
                                </div>
                                <button
                                    onClick={handleInviteByEmail}
                                    className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors text-sm font-medium"
                                >
                                    {t.groupModal?.invite || 'Invite'}
                                </button>
                            </div>
                            <p className="mt-2 text-xs text-gray-500">
                                {t.groupModal?.inviteEmailSent || 'An invitation email will be sent to this address'}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="border-t border-gray-200 p-4 flex justify-end gap-3">
                    <button
                        onClick={handleClose}
                        className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                    >
                        {t.common?.cancel || 'Cancel'}
                    </button>
                    <button
                        onClick={handleFinish}
                        className="px-6 py-2 bg-gradient-to-r from-linkoo-purple to-linkoo-pink text-white rounded-lg font-medium hover:opacity-90 transition-opacity"
                    >
                        {t.groupModal?.finish || 'Finish'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AddMembersModal;
